import { WorkTimeEntryBreak } from 'record-it-time-common/entity/entities/WorkTimeEntry/WorkTimeEntryDto';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { DateUtils } from 'record-it-utils/utils/DateUtils/DateUtils';
import { ImmutableStopwatchInfo } from '../ImmutableStopwatch/ImmutableStopwatch';

/**
 * A class to evaluate a stopwatch to reconstruct a workday
 */
export class StopwatchWorkDayEvaluator {
  private static readonly minimumBreakLength = 60 * 1000; // in ms

  public evaluate(
    stopwatchInfo: ImmutableStopwatchInfo
  ): WorkDayEvaluationData {
    const measuredTimes = this.getSimplifiedMeasuredTimes(stopwatchInfo);

    return {
      startDate: this.getStartDate(measuredTimes),
      endDate: this.getEndDate(measuredTimes),
      breaks: this.getBreaks(measuredTimes)
    };
  }

  private getSimplifiedMeasuredTimes(
    stopwatchInfo: ImmutableStopwatchInfo
  ): Array<MeasuredTime> {
    const simplifiedMeasuredTimes: Array<MeasuredTime> = [];

    for (const measuredTime of this.getAllMeasuredTimes(stopwatchInfo)) {
      const lastSimplifiedMeasuredTime =
        simplifiedMeasuredTimes[simplifiedMeasuredTimes.length - 1];

      if (
        lastSimplifiedMeasuredTime &&
        measuredTime.startDate.getTime() -
          lastSimplifiedMeasuredTime.endDate.getTime() <
          StopwatchWorkDayEvaluator.minimumBreakLength
      ) {
        simplifiedMeasuredTimes[simplifiedMeasuredTimes.length - 1] = {
          ...lastSimplifiedMeasuredTime,
          endDate: measuredTime.endDate
        };
      } else {
        simplifiedMeasuredTimes.push(measuredTime);
      }
    }

    return simplifiedMeasuredTimes;
  }

  private getAllMeasuredTimes(
    stopwatchInfo: ImmutableStopwatchInfo
  ): Array<MeasuredTime> {
    const startTime = stopwatchInfo.getStartTime();
    const measuredTimes = [
      ...stopwatchInfo.getMeasuredTimes(),
      ...(startTime ? [{ startTime, endTime: Date.now() }] : []) // the stopwatch is currently running, so we simulate the last slot to simplify handling the MeasuredTimes
    ].map<MeasuredTime>((measuredTime) => ({
      startDate: DateUtils.withCurrentTimezone().getDate(
        measuredTime.startTime
      ),
      endDate: DateUtils.withCurrentTimezone().getDate(measuredTime.endTime)
    }));

    if (!measuredTimes.length) {
      throw new Error("A stopwatch without measurements can't be evaluated");
    }

    return this.roundMeasuredTimes(measuredTimes);
  }

  private roundMeasuredTimes(
    measuredTimes: Array<MeasuredTime>
  ): Array<MeasuredTime> {
    return measuredTimes.map((measuredTime) => ({
      startDate: this.roundStartDate(measuredTime.startDate),
      endDate: this.roundEndDate(measuredTime.endDate)
    }));
  }

  private roundStartDate(startDate: Date): Date {
    const date = new Date(startDate);

    date.setSeconds(0);
    date.setMilliseconds(0);

    return date;
  }

  private roundEndDate(endDate: Date): Date {
    const date = new Date(endDate);

    if (date.getSeconds() > 0 || date.getMilliseconds() > 0) {
      date.setMinutes(date.getMinutes() + 1, 0, 0);
    }

    return date;
  }

  private getStartDate(measuredTimes: Array<MeasuredTime>): Date {
    const firstMeasuredTime = measuredTimes[0];
    // should never happen, since an exception in getAllMeasuredTimes ist thrown if there are no entries
    assertNotNullOrUndefined(firstMeasuredTime, 'no firstMeasuredTimeFound');
    return firstMeasuredTime.startDate;
  }

  private getEndDate(measuredTimes: Array<MeasuredTime>): Date {
    const lastMeasuredTime = measuredTimes[measuredTimes.length - 1];
    assertNotNullOrUndefined(lastMeasuredTime, 'no lastMeasuredTime');
    return lastMeasuredTime.endDate;
  }

  private getBreaks(
    measuredTimes: Array<MeasuredTime>
  ): Array<WorkTimeEntryBreak> {
    const breaks: Array<WorkTimeEntryBreak> = [];

    for (let i = 1; i < measuredTimes.length; i++) {
      const previousMeasuredTime = measuredTimes[i - 1] as MeasuredTime;
      const measuredTime = measuredTimes[i] as MeasuredTime;

      breaks.push({
        index: breaks.length,
        startDate: previousMeasuredTime.endDate.toISOString(),
        endDate: measuredTime.startDate.toISOString()
      });
    }

    return breaks;
  }
}

export type WorkDayEvaluationData = {
  startDate: Date;
  endDate: Date;
  breaks: Array<WorkTimeEntryBreak>;
};

type MeasuredTime = {
  startDate: Date;
  endDate: Date;
};
